import { CartOrderLineComponent } from '../../../../../../../../types/cart';

export { collapseComponents } from '../../../../../../../utils/orderLineUtils';

export type ExpandedComponent = CartOrderLineComponent & {
  instanceId: string;
  components?: ExpandedComponent[];
};

export interface NavigationSegment {
  instanceId: string;
  itemVariantId: string;
  name: string;
}

/** Expand components with unitQuantity > 1 into individual entries, each with a unique instanceId */
export const expandComponents = (components: CartOrderLineComponent[]): ExpandedComponent[] => {
  let counter = 0;

  const expand = (comps: CartOrderLineComponent[]): ExpandedComponent[] =>
    comps.flatMap((component) => {
      const qty = component.unitQuantity ?? 1;
      return Array.from({ length: qty }, () => ({
        ...component,
        unitQuantity: 1,
        instanceId: String(counter++),
        components: component.components ? expand(component.components) : undefined,
      }));
    });

  return expand(components);
};

/** Find the component pointed to by the last segment of the path */
export const findComponentAtPath = (
  rootComponents: ExpandedComponent[],
  path: NavigationSegment[]
): ExpandedComponent | undefined => {
  if (path.length === 0) return undefined;

  let components = rootComponents;

  for (let i = 0; i < path.length; i++) {
    const match = components.find((c) => c.instanceId === path[i]!.instanceId);
    if (!match) return undefined;
    if (i === path.length - 1) return match;
    if (!match.components) return undefined;
    components = match.components;
  }

  return undefined;
};

/** Check if a component has nested composite children */
export const isCompositeComponent = (component: ExpandedComponent): boolean =>
  Boolean(component.components && component.components.length > 0);

/** Strip instanceId from an ExpandedComponent, returning a plain CartOrderLineComponent */
export const toBaseComponent = (
  ec: ExpandedComponent,
  overrides?: Partial<CartOrderLineComponent>
): CartOrderLineComponent => ({
  id: ec.id,
  name: ec.name,
  quantity: ec.quantity,
  itemVariantId: ec.itemVariantId,
  price: ec.price,
  takeawayPrice: ec.takeawayPrice,
  totalPrice: ec.totalPrice,
  totalTakeawayPrice: ec.totalTakeawayPrice,
  options: ec.options,
  comment: ec.comment,
  commentId: ec.commentId,
  courseId: ec.courseId,
  unitQuantity: ec.unitQuantity,
  menuSectionId: ec.menuSectionId,
  sectionProductId: ec.sectionProductId,
  isCompositeProduct: ec.isCompositeProduct,
  ...overrides,
});
